// MARK://dhk8/admin-shell/pages/modules/CatalogPage
// Módulo 1 — Menú / Catálogo. CRUD de productos, categorías, variantes y listas de
// precios + toggle de disponibilidad. Tabs across the catalog surfaces; wires the
// dhk8-catalog-* clients (REST + PREVIEW_STUB mock fallback).
//
// UX/DS pasada (Gustavo 2026-06-22): listas → DataTable + FilterBar + SummaryStrip;
// estados → StatusPill (Inactivo/Agotado = pill rojo vía lifecycleTone); modales →
// SectionedSheet + FormSection; borrar → useConfirm; guardar/borrar → refetch. Clic
// en fila de producto → ficha de detalle (/catalog/products/:id).
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, Pencil } from 'lucide-react'
import * as cat from '../../lib/clients/catalog.js'
import { listStations, type PrintStation } from '../../lib/clients/print-stations.js'
import * as M from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn, parseMxnToCents } from '../../lib/money.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import TaxRatePicker from '../../components/TaxRatePicker.js'
import FilterBar from '../../components/FilterBar.js'
import { SummaryStrip, type StatCardProps } from '../../components/StatCard.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import UnitPicker from '../../components/UnitPicker.js'
import SatCodePicker from '../../components/SatCodePicker.js'
import MoneyInput from '../../components/MoneyInput.js'
import ImageUploader from '../../components/ImageUploader.js'
import { useToast } from '../../components/Toast.js'
import { useConfirm } from '../../components/ConfirmDialog.js'

// Dedup (decisión Gustavo, dirección b): Modificadores y Combos viven como ítems
// del menú con su página dedicada (/catalog/modifiers, /combos) — más completas y
// personalizables por permisos. Se retiran de las pestañas del Catálogo para que
// cada entidad tenga UN solo hogar.
type Tab = 'productos' | 'categorias' | 'variantes' | 'precios'
const TABS: { id: Tab; label: string }[] = [
  { id: 'productos', label: 'Productos' }, { id: 'categorias', label: 'Categorías' },
  { id: 'variantes', label: 'Variantes' }, { id: 'precios', label: 'Listas de precios' },
]

const PAGE_SIZE = 12

export default function CatalogPage() {
  const [tab, setTab] = useState<Tab>('productos')
  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div><h1 className="adm-page__title">Menú / Catálogo</h1><p className="adm-page__sub">Productos, categorías, variantes y precios</p></div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {TABS.map((tt) => <button key={tt.id} className={`adm-btn adm-btn--sm${tab === tt.id ? ' adm-btn--primary' : ''}`} onClick={() => setTab(tt.id)}>{tt.label}</button>)}
      </div>
      {tab === 'productos' && <ProductsTab />}
      {tab === 'categorias' && <CategoriesTab />}
      {tab === 'variantes' && <VariantsTab />}
      {tab === 'precios' && <PriceListsTab />}
    </div>
  )
}

// ── Productos ──────────────────────────────────────────────────────────────
function ProductsTab() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const confirm = useConfirm()
  const cats = useAsync((s) => cat.listCategories(s))
  const prods = useAsync((s) => cat.listProducts(s))

  // Filtros / orden / paginación client-side (la lista completa ya llega del grafo).
  const [search, setSearch] = useState('')
  const [catFilter, setCatFilter] = useState('')
  const [availFilter, setAvailFilter] = useState('')
  const [sort, setSort] = useState<SortState | null>({ key: 'name', dir: 'asc' })
  const [page, setPage] = useState(1)

  // Modal
  const [editing, setEditing] = useState<M.Product | 'new' | null>(null)
  const blank: M.Product = { id: '', name: '', sku: '', priceCents: 0, categoryId: cats.data?.[0]?.id ?? '', available: true, taxRate: '0.16', imageUrl: null, imageKey: null }
  const [form, setForm] = useState<M.Product>(blank)
  const [busy, setBusy] = useState(false)

  const catName = (id: string) => cats.data?.find((c) => c.id === id)?.name ?? id

  function open(p: M.Product | 'new') { setEditing(p); setForm(p === 'new' ? { ...blank, categoryId: cats.data?.[0]?.id ?? '' } : { ...p }) }

  async function save() {
    setBusy(true)
    try {
      if (editing === 'new') {
        await cat.createProduct({ name: form.name, sku: form.sku, priceCents: form.priceCents, categoryId: form.categoryId, available: form.available, taxRate: form.taxRate, imageUrl: form.imageUrl, description: form.description, unit: form.unit, type: form.type ?? 'PRODUCT', tracksInventory: form.tracksInventory ?? false, consumesRecipe: form.consumesRecipe ?? false, satProductKey: form.satProductKey, satUnitKey: form.satUnitKey })
        toast('Producto creado', 'success')
      } else {
        await cat.updateProduct(form)
        toast('Producto actualizado', 'success')
      }
      setEditing(null); prods.reload()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo guardar el producto', 'danger')
    } finally { setBusy(false) }
  }
  async function remove(p: M.Product) {
    const ok = await confirm({ title: '¿Eliminar producto?', message: `Se eliminará "${p.name}". No se puede deshacer.`, tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    try { await cat.deleteProduct(p.id); toast('Producto eliminado', 'success'); prods.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar', 'danger') }
  }
  async function toggle(p: M.Product) {
    try { await cat.setAvailability(p.id, !p.available); toast(p.available ? 'Marcado como agotado' : 'Marcado como disponible', 'success'); prods.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo cambiar la disponibilidad', 'danger') }
  }

  // KPIs
  const all = prods.data ?? []
  const kpis = useMemo((): StatCardProps[] => {
    const available = all.filter((p) => p.available).length
    const withRecipe = all.filter((p) => p.consumesRecipe).length
    return [
      { label: 'Productos', value: all.length },
      { label: 'Disponibles', value: available, tone: 'ok' },
      { label: 'Agotados', value: all.length - available, tone: all.length - available > 0 ? 'danger' : 'neutral' },
      { label: 'Con receta', value: withRecipe, tone: 'info' },
    ]
  }, [all])

  // Filtro + orden client-side.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let rows = all.filter((p) => {
      if (q && !(p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q))) return false
      if (catFilter && p.categoryId !== catFilter) return false
      if (availFilter === 'available' && !p.available) return false
      if (availFilter === 'unavailable' && p.available) return false
      return true
    })
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1
      rows = [...rows].sort((a, b) => {
        if (sort.key === 'price') return (a.priceCents - b.priceCents) * dir
        if (sort.key === 'category') return catName(a.categoryId).localeCompare(catName(b.categoryId)) * dir
        if (sort.key === 'sku') return a.sku.localeCompare(b.sku) * dir
        return a.name.localeCompare(b.name) * dir
      })
    }
    return rows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, search, catFilter, availFilter, sort, cats.data])

  const total = filtered.length
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const onFilterChange = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1) }

  const columns: DataTableColumn<M.Product>[] = [
    { key: 'name', header: 'Producto', sortable: true, render: (p) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {p.imageUrl
          ? <img src={p.imageUrl} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
          : <span style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--pos-elevated)', flexShrink: 0 }} />}
        <span style={{ fontWeight: 500 }}>{p.name}</span>
      </div>
    ) },
    { key: 'sku', header: 'SKU', sortable: true, render: (p) => <span className="adm-mono">{p.sku || '—'}</span> },
    { key: 'category', header: 'Categoría', sortable: true, render: (p) => catName(p.categoryId) },
    { key: 'price', header: 'Precio', align: 'right', sortable: true, render: (p) => <span className="adm-mono">{mxn(p.priceCents)}</span> },
    { key: 'estado', header: 'Estado', render: (p) => <StatusPill tone={lifecycleTone(p.available)} label={p.available ? 'Disponible' : 'Agotado'} /> },
    { key: 'acciones', header: '', align: 'right', width: '1%', render: (p) => (
      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
        <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => toggle(p)} title="Cambiar disponibilidad">{p.available ? 'Agotar' : 'Activar'}</button>
        <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => open(p)} aria-label="Editar"><Pencil size={15} /></button>
        <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => remove(p)} aria-label="Eliminar"><Trash2 size={15} /></button>
      </div>
    ) },
  ]

  return (
    <>
      {(prods.data || prods.loading) && (
        <div style={{ margin: '4px 0 12px' }}>
          <SummaryStrip items={kpis} />
        </div>
      )}
      {asyncState(prods.loading, prods.error, prods.reload)}
      {prods.data && (
        <DataTable<M.Product>
          columns={columns}
          rows={pageRows}
          getRowKey={(p) => p.id}
          zebra
          sort={sort}
          onSortChange={(s) => { setSort(s); setPage(1) }}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          onRowClick={(p) => navigate(`/catalog/products/${p.id}`)}
          emptyText="Sin productos que coincidan con el filtro."
          toolbar={<FilterBar
            search={search}
            onSearch={onFilterChange(setSearch)}
            searchPlaceholder="Buscar por nombre o SKU…"
            filters={[
              { key: 'cat', label: 'Categoría', value: catFilter, onChange: onFilterChange(setCatFilter), options: [{ value: '', label: 'Todas' }, ...(cats.data ?? []).map((c) => ({ value: c.id, label: c.name }))] },
              { key: 'avail', label: 'Disponibilidad', value: availFilter, onChange: onFilterChange(setAvailFilter), options: [{ value: '', label: 'Todas' }, { value: 'available', label: 'Disponibles' }, { value: 'unavailable', label: 'Agotados' }] },
            ]}
            actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => open('new')}><Plus size={16} /> Nuevo producto</button>}
          />}
        />
      )}

      <ProductSheet
        editing={editing}
        form={form}
        setForm={setForm}
        cats={cats.data ?? []}
        busy={busy}
        onClose={() => setEditing(null)}
        onSave={save}
      />
    </>
  )
}

// Modal seccionado de producto (Identidad / Precio e impuestos / Operación / Media).
function ProductSheet({ editing, form, setForm, cats, busy, onClose, onSave }: {
  editing: M.Product | 'new' | null
  form: M.Product
  setForm: (p: M.Product) => void
  cats: M.Category[]
  busy: boolean
  onClose: () => void
  onSave: () => void
}) {
  return (
    <SectionedSheet
      open={editing != null}
      title={editing === 'new' ? 'Nuevo producto' : 'Editar producto'}
      onClose={onClose}
      footer={<>
        <button className="adm-btn" onClick={onClose} disabled={busy}>Cancelar</button>
        <button className="adm-btn adm-btn--primary" onClick={onSave} disabled={busy || !form.name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
      </>}
    >
      <FormSection title="Identidad">
        <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tacos al pastor…" /></label>
        <label className="adm-field"><span className="adm-field__label">SKU</span><input className="adm-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></label>
        <label className="adm-field"><span className="adm-field__label">Descripción</span><textarea className="adm-input" rows={2} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Detalle opcional para el menú" /></label>
        <label className="adm-field"><span className="adm-field__label">Categoría</span>
          <select className="adm-select" value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
        {editing === 'new' ? (
          <label className="adm-field"><span className="adm-field__label">Tipo</span>
            <select className="adm-select" value={form.type ?? 'PRODUCT'} onChange={(e) => setForm({ ...form, type: e.target.value as M.Product['type'] })}>
              <option value="PRODUCT">Producto</option>
              <option value="SERVICE">Servicio</option>
              <option value="COMBO">Combo</option>
              <option value="INGREDIENT">Insumo</option>
            </select>
          </label>
        ) : form.type && (
          <div className="adm-field"><span className="adm-field__label">Tipo</span><div className="adm-readonly-box">{form.type}</div></div>
        )}
      </FormSection>

      <FormSection title="Precio e impuestos">
        <label className="adm-field"><span className="adm-field__label">Precio (MXN)</span><MoneyInput valueCents={form.priceCents} onChangeCents={(c) => setForm({ ...form, priceCents: c })} /></label>
        <TaxRatePicker value={form.taxRate} onChange={(taxRate) => setForm({ ...form, taxRate })} />
        <UnitPicker label="Unidad" value={form.unit ?? ''} onChange={(unit) => setForm({ ...form, unit })} />
        <div className="adm-field"><span className="adm-field__label">Clave SAT producto (claveProdServ)</span><SatCodePicker value={form.satProductKey ?? ''} onChange={(satProductKey) => setForm({ ...form, satProductKey })} /></div>
        <UnitPicker mode="sat" label="Clave SAT unidad (claveUnidad)" value={form.satUnitKey ?? ''} onChange={(satUnitKey) => setForm({ ...form, satUnitKey })} />
      </FormSection>

      <FormSection title="Operación">
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} /> Disponible</label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.tracksInventory ?? false} onChange={(e) => setForm({ ...form, tracksInventory: e.target.checked })} /> Lleva inventario propio</label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.consumesRecipe ?? false} onChange={(e) => setForm({ ...form, consumesRecipe: e.target.checked })} /> Consume receta al vender</label>
      </FormSection>

      <FormSection title="Media">
        <div className="adm-field"><span className="adm-field__label">Imagen del producto</span>
          <ImageUploader value={form.imageUrl ?? null} onChange={(url) => setForm({ ...form, imageUrl: url, imageKey: url ? form.imageKey : null })} />
        </div>
      </FormSection>
      <style>{`.adm-readonly-box { font-family: var(--font-mono, var(--font-sans)); font-size: 13px; color: var(--pos-fg-secondary); padding: 8px 10px; border: 1px solid var(--pos-separator); border-radius: var(--pos-radius-md, 8px); background: var(--pos-elevated); }`}</style>
    </SectionedSheet>
  )
}

// ── Categorías ─────────────────────────────────────────────────────────────
function CategoriesTab() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const confirm = useConfirm()
  const cats = useAsync((s) => cat.listCategories(s))
  const stations = useAsync((s) => listStations(s))
  const [editing, setEditing] = useState<M.Category | 'new' | null>(null)
  const [form, setForm] = useState<M.Category>({ id: '', name: '', sortOrder: 1, active: true, stationId: null, publicVisible: true })
  const [busy, setBusy] = useState(false)
  function open(c: M.Category | 'new') {
    setEditing(c)
    setForm(c === 'new'
      ? { id: '', name: '', sortOrder: (cats.data?.length ?? 0) + 1, active: true, stationId: null, publicVisible: true }
      : { ...c, stationId: c.stationId ?? null })
  }
  async function save() {
    setBusy(true)
    try {
      if (editing === 'new') await cat.createCategory({ name: form.name, sortOrder: form.sortOrder, active: form.active, stationId: form.stationId ?? null, publicVisible: form.publicVisible })
      else await cat.updateCategory(form)
      toast(editing === 'new' ? 'Categoría creada' : 'Categoría actualizada', 'success')
      setEditing(null); cats.reload()
    } catch (e) { toast(e instanceof Error ? e.message : 'No se pudo guardar', 'danger') } finally { setBusy(false) }
  }
  async function remove(c: M.Category) {
    const ok = await confirm({ title: '¿Eliminar categoría?', message: `Se eliminará "${c.name}".`, tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    try { await cat.deleteCategory(c.id); toast('Categoría eliminada', 'success'); cats.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar', 'danger') }
  }
  const stationName = (id: string | null | undefined): string | null => {
    if (!id) return null
    return stations.data?.find((s: PrintStation) => s.id === id)?.name ?? '(estación no encontrada)'
  }
  return (
    <>
      <div className="adm-page__actions" style={{ marginTop: 12 }}><button className="adm-btn adm-btn--primary" onClick={() => open('new')}><Plus size={16} /> Nueva categoría</button></div>
      {asyncState(cats.loading, cats.error, cats.reload)}
      {cats.data && <div className="adm-card adm-card--pad0"><div className="adm-list">
        {cats.data.length === 0 && <div className="adm-empty">Sin categorías</div>}
        {[...cats.data].sort((a, b) => a.sortOrder - b.sortOrder).map((c) => {
          const sName = stationName(c.stationId)
          return (
            <div key={c.id} className="adm-row" role="button" tabIndex={0} style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/catalog/categories/${c.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/catalog/categories/${c.id}`) } }}>
              <div className="adm-row__main">
                <div className="adm-row__title">{c.name}</div>
                <div className="adm-row__meta">orden {c.sortOrder}{sName ? ` · estación: ${sName}` : ' · sin estación'}</div>
              </div>
              <div className="adm-row__tail" onClick={(e) => e.stopPropagation()}>
                {!c.active && <StatusPill tone={lifecycleTone(false)} label="Inactiva" />}
                {!c.publicVisible && <StatusPill tone="neutral" label="Interna" />}
                <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => remove(c)} aria-label="Eliminar"><Trash2 size={15} /></button>
              </div>
            </div>
          )
        })}
      </div></div>}
      <SectionedSheet open={editing != null} title={editing === 'new' ? 'Nueva categoría' : 'Editar categoría'} onClose={() => setEditing(null)}
        footer={<><button className="adm-btn" onClick={() => setEditing(null)} disabled={busy}>Cancelar</button><button className="adm-btn adm-btn--primary" onClick={save} disabled={busy || !form.name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button></>}>
        <FormSection title="Identidad">
          <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label className="adm-field"><span className="adm-field__label">Orden</span><input className="adm-input" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} /></label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Activa</label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <input type="checkbox" checked={form.publicVisible} onChange={(e) => setForm({ ...form, publicVisible: e.target.checked })} style={{ marginTop: 3 }} />
            <span>Visible en menú público
              <span className="adm-row__meta" style={{ display: 'block' }}>Apágalo para categorías internas (insumos): siguen en POS/recetas/inventario pero no las ve el comensal.</span>
            </span>
          </label>
        </FormSection>
        <FormSection title="Operación">
          <label className="adm-field">
            <span className="adm-field__label">Estación de impresión</span>
            <select className="adm-select" value={form.stationId ?? ''} onChange={(e) => setForm({ ...form, stationId: e.target.value || null })} disabled={stations.loading}>
              <option value="">Sin estación</option>
              {(stations.data ?? []).map((s: PrintStation) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <span className="adm-row__meta" style={{ marginTop: 4 }}>Productos de esta categoría se enrutan a esta estación (cocina, barra…).</span>
          </label>
        </FormSection>
      </SectionedSheet>
    </>
  )
}

// ── Variantes (modelo multi-eje A5) ─────────────────────────────────────────
// El producto tiene EJES de variación (ej. "Tamaño"); cada eje tiene OPCIONES
// (Chico/Grande con Δ precio); un SKU es una combinación de opciones. Ejes y
// opciones son EDITABLES (upsert con id) además de crearse.
function VariantsTab() {
  const { toast } = useToast()
  const confirm = useConfirm()
  const prods = useAsync((s) => cat.listProducts(s))
  const [productId, setProductId] = useState('')
  const effectiveProductId = productId || prods.data?.[0]?.id || ''
  const axes = useAsync((s) => (effectiveProductId ? cat.listVariantAxes(effectiveProductId, s) : Promise.resolve([])), [effectiveProductId])
  const skus = useAsync((s) => (effectiveProductId ? cat.listVariantSkus(effectiveProductId, s) : Promise.resolve([])), [effectiveProductId])

  // axis sheet (crear o editar)
  const [axisEditing, setAxisEditing] = useState<cat.UiVariantAxis | 'new' | null>(null)
  const [axisName, setAxisName] = useState('')
  // option sheet (crear o editar) — guardamos el axisId destino + la opción si edita
  const [optState, setOptState] = useState<{ axisId: string; option: cat.UiVariantOption | null } | null>(null)
  const [optForm, setOptForm] = useState({ label: '', priceDeltaCents: 0 })
  const [busy, setBusy] = useState(false)

  function openAxisNew() { setAxisEditing('new'); setAxisName('') }
  function openAxisEdit(a: cat.UiVariantAxis) { setAxisEditing(a); setAxisName(a.name) }
  async function saveAxis() {
    if (!effectiveProductId || !axisName.trim()) return
    setBusy(true)
    try {
      await cat.upsertVariantAxis({ ...(axisEditing !== 'new' && axisEditing ? { id: axisEditing.id } : {}), productId: effectiveProductId, name: axisName.trim(), sortOrder: axisEditing !== 'new' && axisEditing ? axisEditing.sortOrder : (axes.data?.length ?? 0) })
      toast(axisEditing === 'new' ? 'Eje creado' : 'Eje actualizado', 'success')
      setAxisEditing(null); setAxisName(''); axes.reload()
    } catch (e) { toast(e instanceof Error ? e.message : 'No se pudo guardar el eje', 'danger') } finally { setBusy(false) }
  }
  async function removeAxis(a: cat.UiVariantAxis) {
    const ok = await confirm({ title: '¿Eliminar eje?', message: `Se eliminará "${a.name}" y todas sus opciones.`, tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    try { await cat.deleteVariantAxis(a.id); toast('Eje eliminado', 'success'); axes.reload(); skus.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar', 'danger') }
  }

  function openOptNew(axisId: string) { setOptState({ axisId, option: null }); setOptForm({ label: '', priceDeltaCents: 0 }) }
  function openOptEdit(axisId: string, o: cat.UiVariantOption) { setOptState({ axisId, option: o }); setOptForm({ label: o.label, priceDeltaCents: o.priceDeltaCents }) }
  async function saveOpt() {
    if (!optState || !optForm.label.trim()) return
    const axis = axes.data?.find((a) => a.id === optState.axisId)
    setBusy(true)
    try {
      await cat.upsertVariantOption({ ...(optState.option ? { id: optState.option.id } : {}), axisId: optState.axisId, label: optForm.label.trim(), priceDeltaCents: optForm.priceDeltaCents, sortOrder: optState.option ? optState.option.sortOrder : (axis?.options.length ?? 0) })
      toast(optState.option ? 'Opción actualizada' : 'Opción creada', 'success')
      setOptState(null); setOptForm({ label: '', priceDeltaCents: 0 }); axes.reload()
    } catch (e) { toast(e instanceof Error ? e.message : 'No se pudo guardar la opción', 'danger') } finally { setBusy(false) }
  }
  async function removeOption(o: cat.UiVariantOption) {
    const ok = await confirm({ title: '¿Eliminar opción?', message: `Se eliminará "${o.label}".`, tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    try { await cat.deleteVariantOption(o.id); toast('Opción eliminada', 'success'); axes.reload(); skus.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar', 'danger') }
  }
  async function removeSku(skuId: string) {
    const ok = await confirm({ title: '¿Eliminar SKU?', tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    try { await cat.deleteVariantSku(skuId); toast('SKU eliminado', 'success'); skus.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar', 'danger') }
  }

  const optionLabel = (optionId: string): string => {
    for (const a of axes.data ?? []) { const o = a.options.find((x) => x.id === optionId); if (o) return `${a.name}: ${o.label}` }
    return optionId
  }

  return (
    <>
      <label className="adm-field" style={{ maxWidth: 420, marginTop: 12 }}>
        <span className="adm-field__label">Producto</span>
        <select className="adm-select" value={effectiveProductId} onChange={(e) => setProductId(e.target.value)}>
          {(prods.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}{p.sku ? ` · ${p.sku}` : ''}</option>)}
        </select>
      </label>

      {asyncState(axes.loading, axes.error, axes.reload)}

      {axes.data && effectiveProductId && (
        <>
          <div className="adm-page__actions" style={{ marginTop: 12 }}>
            <button className="adm-btn adm-btn--primary" onClick={openAxisNew}><Plus size={16} /> Nuevo eje</button>
          </div>
          <div className="adm-card adm-card--pad0"><div className="adm-list">
            {axes.data.length === 0 && <div className="adm-empty">Sin ejes de variación. Crea uno (ej. "Tamaño") para empezar.</div>}
            {axes.data.map((a) => (
              <div key={a.id} className="adm-row" style={{ alignItems: 'flex-start' }}>
                <div className="adm-row__main">
                  <div className="adm-row__title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {a.name}
                    <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openAxisEdit(a)} aria-label="Editar eje"><Pencil size={13} /></button>
                    <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openOptNew(a.id)}><Plus size={13} /> Opción</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6 }}>
                    {a.options.length === 0 && <span className="adm-row__meta">Sin opciones todavía</span>}
                    {a.options.map((o) => (
                      <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                        <span>{o.label}</span>
                        <span className="adm-row__meta adm-mono">{o.priceDeltaCents >= 0 ? '+' : ''}{mxn(o.priceDeltaCents)}</span>
                        <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openOptEdit(a.id, o)} aria-label="Editar opción"><Pencil size={13} /></button>
                        <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => removeOption(o)} aria-label="Quitar opción"><Trash2 size={13} /></button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="adm-row__tail"><button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => removeAxis(a)} aria-label="Eliminar eje"><Trash2 size={15} /></button></div>
              </div>
            ))}
          </div></div>

          <div className="adm-form-section" style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--pos-fg-tertiary)', margin: '18px 0 6px' }}>SKUs de variante</div>
          <div className="adm-card adm-card--pad0"><div className="adm-list">
            {(skus.data ?? []).length === 0 && <div className="adm-empty">Sin SKUs generados.</div>}
            {(skus.data ?? []).map((s) => (
              <div key={s.id} className="adm-row">
                <div className="adm-row__main">
                  <div className="adm-row__title">{s.sku}</div>
                  <div className="adm-row__meta">{s.optionIds.map(optionLabel).join(' · ') || 'sin opciones'} · {s.priceMode === 'ABSOLUTE' ? `precio ${s.priceCents != null ? mxn(s.priceCents) : '—'}` : 'suma de deltas'}</div>
                </div>
                <div className="adm-row__tail">
                  <StatusPill tone={lifecycleTone(s.active)} label={s.active ? 'Activo' : 'Inactivo'} />
                  <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => removeSku(s.id)} aria-label="Eliminar SKU"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div></div>
        </>
      )}

      <SectionedSheet open={axisEditing != null} title={axisEditing === 'new' ? 'Nuevo eje de variación' : 'Editar eje'} onClose={() => setAxisEditing(null)} footer={<><button className="adm-btn" onClick={() => setAxisEditing(null)} disabled={busy}>Cancelar</button><button className="adm-btn adm-btn--primary" onClick={saveAxis} disabled={busy || !axisName.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button></>}>
        <FormSection title="Identidad">
          <label className="adm-field"><span className="adm-field__label">Nombre del eje</span><input className="adm-input" value={axisName} onChange={(e) => setAxisName(e.target.value)} placeholder="Tamaño, Tipo de leche…" /></label>
        </FormSection>
      </SectionedSheet>

      <SectionedSheet open={optState != null} title={optState?.option ? 'Editar opción' : 'Nueva opción'} onClose={() => setOptState(null)} footer={<><button className="adm-btn" onClick={() => setOptState(null)} disabled={busy}>Cancelar</button><button className="adm-btn adm-btn--primary" onClick={saveOpt} disabled={busy || !optForm.label.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button></>}>
        <FormSection title="Identidad">
          <label className="adm-field"><span className="adm-field__label">Etiqueta</span><input className="adm-input" value={optForm.label} onChange={(e) => setOptForm({ ...optForm, label: e.target.value })} placeholder="Chico, Grande, Deslactosada…" /></label>
        </FormSection>
        <FormSection title="Precio">
          <label className="adm-field"><span className="adm-field__label">Δ Precio (MXN)</span><input className="adm-input" inputMode="decimal" value={(optForm.priceDeltaCents / 100).toString()} onChange={(e) => setOptForm({ ...optForm, priceDeltaCents: parseMxnToCents(e.target.value) })} /></label>
        </FormSection>
      </SectionedSheet>
    </>
  )
}

// ── Listas de precios ──────────────────────────────────────────────────────
// Lista las listas; "Nueva lista" se queda como sheet aquí. El clic en una fila
// navega a su ficha (/catalog/price-lists/:id), donde vive el editor de overrides
// (precio por producto) y la edición de nombre/estado. Toggle de estado y borrar
// quedan inline en la fila (stopPropagation para no disparar la navegación).
function PriceListsTab() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const confirm = useConfirm()
  const pls = useAsync((s) => cat.listPriceLists(s))
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  async function add() {
    setBusy(true)
    try { await cat.createPriceList({ name, active: false, overrides: {} }); toast('Lista creada', 'success'); setCreating(false); setName(''); pls.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo crear', 'danger') } finally { setBusy(false) }
  }
  async function toggle(pl: M.PriceList) {
    try { await cat.setPriceListActive(pl.id, !pl.active); toast(pl.active ? 'Lista desactivada' : 'Lista activada', 'success'); pls.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo cambiar el estado', 'danger') }
  }
  async function remove(pl: M.PriceList) {
    const ok = await confirm({ title: '¿Eliminar lista de precios?', message: `Se eliminará "${pl.name}".`, tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    try { await cat.deletePriceList(pl.id); toast('Lista eliminada', 'success'); pls.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar', 'danger') }
  }

  return (
    <>
      <div className="adm-page__actions" style={{ marginTop: 12 }}><button className="adm-btn adm-btn--primary" onClick={() => setCreating(true)}><Plus size={16} /> Nueva lista</button></div>
      {asyncState(pls.loading, pls.error, pls.reload)}
      {pls.data && <div className="adm-card adm-card--pad0"><div className="adm-list">
        {pls.data.length === 0 && <div className="adm-empty">Sin listas de precios</div>}
        {pls.data.map((pl) => (
          <div key={pl.id} className="adm-row" role="button" tabIndex={0} style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/catalog/price-lists/${pl.id}`)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/catalog/price-lists/${pl.id}`) } }}>
            <div className="adm-row__main">
              <div className="adm-row__title">{pl.name}</div>
              <div className="adm-row__meta">{Object.keys(pl.overrides).length} {Object.keys(pl.overrides).length === 1 ? 'precio especial' : 'precios especiales'}</div>
            </div>
            <div className="adm-row__tail" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => toggle(pl)} title="Cambiar estado" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}><StatusPill tone={lifecycleTone(pl.active)} label={pl.active ? 'Activa' : 'Inactiva'} /></button>
              <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => remove(pl)} aria-label="Eliminar"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div></div>}

      <SectionedSheet open={creating} title="Nueva lista de precios" onClose={() => setCreating(false)} footer={<><button className="adm-btn" onClick={() => setCreating(false)} disabled={busy}>Cancelar</button><button className="adm-btn adm-btn--primary" onClick={add} disabled={busy || !name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button></>}>
        <FormSection title="Identidad">
          <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Happy hour / Mayoreo…" /></label>
        </FormSection>
      </SectionedSheet>
    </>
  )
}
